import LoginService from '@/services/LoginService';
import store from '../store';
import { parseRolesFromScopes } from '../utils/AuthGuard';

export interface IUser {
  id: string;
  name: string;
  /** @deprecated Use `roles` array instead. Kept for backward compatibility. */
  role: string;
  roles: string[];
  token: string;
  tokenExpiry: number;
}

export class User implements IUser {
  public roles: string[];

  constructor(
    public id: string,
    public name: string,
    roles: string | string[],
    public token: string,
    public tokenExpiry: number
  ) {
    // Accept either a single role string (backward compat) or an array.
    this.roles = Array.isArray(roles) ? roles : [roles];
  }

  /** Backward-compatible getter: returns the highest-priority role. */
  get role(): string {
    return this.roles[0] ?? '';
  }

  public isAuthenticated() {
    return (
      typeof this.token === 'string' &&
      this.token.length > 0 &&
      Number.isFinite(this.tokenExpiry) &&
      this.tokenExpiry > Date.now()
    );
  }

  public async refreshToken(): Promise<boolean> {
    const session = store.state.user;
    try {
      if (!this.isAuthenticated()) throw new Error('Session expired');
      const result = (await LoginService.refreshToken()) as {
        accessToken?: unknown;
        expiresIn?: unknown;
        scope?: unknown;
      } | null;
      if (store.state.user !== session) return false;
      if (
        !result ||
        typeof result.accessToken !== 'string' ||
        !result.accessToken ||
        typeof result.expiresIn !== 'number' ||
        !Number.isFinite(result.expiresIn) ||
        result.expiresIn <= 0
      ) {
        throw new Error('Invalid refreshed session');
      }
      this.token = result.accessToken;
      this.tokenExpiry = Date.now() + result.expiresIn * 1000;
      this.roles = parseRolesFromScopes(
        typeof result.scope === 'string' ? result.scope : ''
      );
      await store.dispatch('login', this);
      return true;
    } catch {
      if (store.state.user === session) await store.dispatch('login', null);
      return false;
    }
  }
}
